import type { CSSProperties } from 'react';
import { CFG_CHIP_SIZE } from '../../lib/configuratorChip';
import type { PricePoint } from '../../types';
import {
  matrixPointPrice,
  roundUpOnGrid,
  secondaryValuesForPrimary,
  type DimensionQuote,
  type MatrixDimKey,
  type MatrixLayout,
} from '../../lib/priceMatrixDimensions';

type Props = {
  matrix: PricePoint[];
  layout: MatrixLayout;
  primaryLabel: string;
  secondaryLabel: string;
  selectedPrimary: number | null;
  selectedSecondary: number | null;
  onSelectPrimary: (v: number | null) => void;
  onSelectSecondary: (v: number | null) => void;
  customDimInput: string;
  onCustomDimInput: (v: string) => void;
  customSecondaryDimInput: string;
  onCustomSecondaryDimInput: (v: string) => void;
  dimensionQuote: DimensionQuote;
  showRoomHeightNote: boolean;
  clientHeightM: string;
  onClientHeightM: (v: string) => void;
  hapticSelection: () => void;
};

function selectedStyle(isSelected: boolean): CSSProperties {
  return isSelected
    ? {
        background: 'rgba(99,179,237,0.22)',
        border: '1px solid var(--cyan)',
        boxShadow: '0 0 22px rgba(99,179,237,0.32), inset 0 1px 0 rgba(255,255,255,0.08)',
        transform: 'scale(1.05)',
        transition: 'transform 240ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 320ms ease',
      }
    : {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid var(--border-subtle)',
        transition: 'transform 200ms ease',
      };
}

function SizeChipGrid({
  values,
  selected,
  onSelect,
  matrix,
  dimKey,
  secondKey,
  secondValue,
  hidePrice,
  hapticSelection,
}: {
  values: number[];
  selected: number | null;
  onSelect: (v: number | null) => void;
  matrix: PricePoint[];
  dimKey: MatrixDimKey;
  secondKey?: MatrixDimKey | null;
  secondValue?: number | null;
  hidePrice?: boolean;
  hapticSelection: () => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:gap-3">
      {values.map((dim) => {
        const price =
          secondKey && secondValue != null
            ? matrixPointPrice(matrix, dimKey, secondKey, dim, secondValue)
            : matrixPointPrice(matrix, dimKey, null, dim, null);
        const isSelected = selected !== null && Math.abs(selected - dim) < 1e-6;
        return (
          <button
            key={dim}
            type="button"
            onClick={() => {
              onSelect(isSelected ? null : dim);
              hapticSelection();
            }}
            className={CFG_CHIP_SIZE}
            style={selectedStyle(isSelected)}
          >
            <span className="font-bold text-base" style={{ color: isSelected ? 'var(--cyan-bright)' : 'var(--text-1)' }}>
              {dim} м
            </span>
            {!hidePrice && price != null && (
              <span className="text-[11px] mt-1 font-medium" style={{ color: isSelected ? 'var(--cyan)' : 'var(--text-3)' }}>
                {price.toLocaleString('ru-RU')} ₽
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function CustomDimInputBlock({
  label,
  customDimInput,
  onCustomDimInput,
  onSelect,
  selectedValue,
  fallbackGridValue,
  maxDim,
  hapticSelection,
}: {
  label: string;
  customDimInput: string;
  onCustomDimInput: (v: string) => void;
  onSelect: (v: number | null) => void;
  selectedValue: number | null;
  fallbackGridValue: number | null;
  maxDim: number | null;
  hapticSelection: () => void;
}) {
  return (
    <div
      className="mt-3 rounded-xl px-3 py-3 min-w-0"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
    >
      <p className="text-[11px] mb-2 leading-snug" style={{ color: 'var(--text-3)' }}>
        Свой размер ({label.toLowerCase()}): шаг ±0,1 м
      </p>
      <div className="grid gap-2 min-w-0">
        <input
          type="number"
          min={0.1}
          step={0.1}
          inputMode="decimal"
          className="input-glass w-full min-w-0 box-border px-3 py-2.5 rounded-xl text-sm"
          value={customDimInput}
          onChange={(e) => {
            const value = e.target.value;
            onCustomDimInput(value);
            const parsed = Number(value);
            if (Number.isFinite(parsed) && parsed > 0) onSelect(parsed);
          }}
          placeholder={selectedValue != null ? String(selectedValue) : 'Например 2.7'}
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className="press w-full min-h-[44px] rounded-xl text-sm font-bold"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
            onClick={() => {
              const base = selectedValue ?? (Number(customDimInput) || fallbackGridValue || 1);
              const next = Math.max(0.1, Math.round((base - 0.1) * 10) / 10);
              onSelect(next);
              onCustomDimInput(String(next));
              hapticSelection();
            }}
          >
            −0,1 м
          </button>
          <button
            type="button"
            className="press w-full min-h-[44px] rounded-xl text-sm font-bold"
            style={{ background: 'rgba(99,179,237,0.18)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
            onClick={() => {
              const base = selectedValue ?? (Number(customDimInput) || fallbackGridValue || 1);
              const next = Math.round((base + 0.1) * 10) / 10;
              onSelect(next);
              onCustomDimInput(String(next));
              hapticSelection();
            }}
          >
            +0,1 м
          </button>
        </div>
      </div>
      {maxDim != null && (
        <p className="text-[10px] mt-2" style={{ color: 'var(--text-3)' }}>
          Максимум в прайсе: {maxDim} м
        </p>
      )}
    </div>
  );
}

function DimensionFeedback({ quote }: { quote: DimensionQuote }) {
  if (!quote.messages.length && !quote.priceOnRequest) return null;
  return (
    <div className="mt-3 space-y-2">
      {quote.messages.map((msg) => (
        <div
          key={msg}
          className="px-2.5 py-2 rounded-lg text-[11px] leading-snug"
          style={{
            background: quote.priceOnRequest ? 'rgba(246,173,85,0.14)' : 'rgba(99,179,237,0.1)',
            color: quote.priceOnRequest ? '#F6AD55' : 'var(--cyan-bright)',
            border: quote.priceOnRequest
              ? '1px solid rgba(246,173,85,0.35)'
              : '1px solid rgba(99,179,237,0.25)',
          }}
        >
          {msg}
        </div>
      ))}
      {quote.priceOnRequest && quote.canAddToCart && (
        <p className="text-[10px] leading-snug px-1" style={{ color: 'var(--text-3)' }}>
          В корзину можно добавить с пометкой «цена по запросу» — менеджер перезвонит и согласует финал.
        </p>
      )}
    </div>
  );
}

export function ConfiguratorSizeCascade({
  matrix,
  layout,
  primaryLabel,
  secondaryLabel,
  selectedPrimary,
  selectedSecondary,
  onSelectPrimary,
  onSelectSecondary,
  customDimInput,
  onCustomDimInput,
  customSecondaryDimInput,
  onCustomSecondaryDimInput,
  dimensionQuote,
  showRoomHeightNote,
  clientHeightM,
  onClientHeightM,
  hapticSelection,
}: Props) {
  if (layout.mode === 'none') return null;

  const primaryKey = layout.mode === '1d' ? layout.key : layout.primary;
  const primaryValues = layout.mode === '1d' ? layout.values : layout.primaryValues;
  const gridPrimary =
    layout.mode === '2d' && selectedPrimary != null
      ? roundUpOnGrid(selectedPrimary, primaryValues)
      : null;
  const secondaryValues =
    layout.mode === '2d' && gridPrimary != null
      ? secondaryValuesForPrimary(matrix, layout.primary, layout.secondary, gridPrimary)
      : [];

  const primaryCustomBlock = (
    <CustomDimInputBlock
      label={primaryLabel}
      customDimInput={customDimInput}
      onCustomDimInput={onCustomDimInput}
      onSelect={onSelectPrimary}
      selectedValue={selectedPrimary}
      fallbackGridValue={primaryValues[0] ?? null}
      maxDim={dimensionQuote.maxPrimary}
      hapticSelection={hapticSelection}
    />
  );

  const secondaryCustomBlock =
    layout.mode === '2d' ? (
      <CustomDimInputBlock
        label={secondaryLabel}
        customDimInput={customSecondaryDimInput}
        onCustomDimInput={onCustomSecondaryDimInput}
        onSelect={onSelectSecondary}
        selectedValue={selectedSecondary}
        fallbackGridValue={secondaryValues[0] ?? null}
        maxDim={dimensionQuote.maxSecondary}
        hapticSelection={hapticSelection}
      />
    ) : null;

  if (layout.mode === '1d') {
    return (
      <div className="space-y-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>
            ◈ {primaryLabel}
          </p>
          <SizeChipGrid
            values={primaryValues}
            selected={selectedPrimary}
            onSelect={onSelectPrimary}
            matrix={matrix}
            dimKey={primaryKey}
            hapticSelection={hapticSelection}
          />
        </div>
        {primaryCustomBlock}
        <p className="text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
          Цена — по прайсу с округлением вверх до ближайшей ячейки сетки.
        </p>
        <DimensionFeedback quote={dimensionQuote} />
        {showRoomHeightNote && (
          <details className="rounded-lg px-2 py-1.5" style={{ background: 'rgba(10,25,47,0.35)', border: '1px solid var(--border-subtle)' }}>
            <summary className="text-[11px] font-semibold cursor-pointer py-1" style={{ color: 'var(--cyan-bright)' }}>
              Высота проёма (для замера, в заказ)
            </summary>
            <input
              type="number"
              min={0}
              step={0.01}
              inputMode="decimal"
              className="input-glass w-full mt-2 px-3 py-2.5 rounded-xl text-sm"
              value={clientHeightM}
              onChange={(e) => onClientHeightM(e.target.value)}
              placeholder="Например 2.5 (м)"
            />
          </details>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>
          ◈ {primaryLabel}
        </p>
        <SizeChipGrid
          values={primaryValues}
          selected={selectedPrimary}
          onSelect={(v) => {
            onSelectPrimary(v);
            onSelectSecondary(null);
          }}
          matrix={matrix}
          dimKey={layout.primary}
          hidePrice
          hapticSelection={hapticSelection}
        />
      </div>

      {selectedPrimary != null && (
        <div
          className="rounded-2xl px-3 py-3 space-y-2"
          style={{
            background: 'rgba(99,179,237,0.06)',
            border: '1px solid rgba(99,179,237,0.2)',
          }}
        >
          <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--cyan-bright)' }}>
            ◈ {secondaryLabel}
          </p>
          <p className="text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
            Для {primaryLabel.toLowerCase()} {selectedPrimary} м — варианты из прайса или свой замер ниже
          </p>
          {secondaryValues.length === 0 ? (
            <p className="text-[11px]" style={{ color: '#F6AD55' }}>
              Нет готовых ячеек для этой ширины — укажите {secondaryLabel.toLowerCase()} вручную ниже.
            </p>
          ) : (
            <SizeChipGrid
              values={secondaryValues}
              selected={selectedSecondary}
              onSelect={onSelectSecondary}
              matrix={matrix}
              dimKey={layout.secondary}
              secondKey={layout.primary}
              secondValue={selectedPrimary}
              hapticSelection={hapticSelection}
            />
          )}
        </div>
      )}

      <div className="space-y-0">
        {primaryCustomBlock}
        {secondaryCustomBlock}
        <p className="text-[10px] mt-2 leading-snug px-0.5" style={{ color: 'var(--text-3)' }}>
          Можно указать любые размеры: калькулятор подскажет, что входит в прайс, а что — по запросу менеджера.
        </p>
        <DimensionFeedback quote={dimensionQuote} />
      </div>
    </div>
  );
}
